import { createAsyncThunk } from '@reduxjs/toolkit';
import {
    setLoading,
    setError,
    setResults,
    LIKES_SOURCE_ID,
} from '../slices/smartRecsSlice';
import { ensureToken } from './djpalThunk';
import { refreshAndRetry } from '../utils/refreshAndRetry';

// Fisher-Yates in place — used to shuffle the recent-track window before
// capping, so big sources surface a slightly different (more fun) crate
// each time instead of the same first/last N.
function shuffleInPlace(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

// Pull the user's most-recent likes (newest-first from the API) up to
// `maxPool`, paging in 100s. Returned newest-first; the caller reverses to
// oldest-first so the shared "most recent = end of array" logic applies.
async function loadRecentLikes(maxPool) {
    const out = [];
    const LIMIT = 100;
    const MAX_PAGES = 5;
    for (let page = 1; page <= MAX_PAGES && out.length < maxPool; page++) {
        const p = page; // bind for the closure (avoids no-loop-func on the mutated `page`)
        const res = await refreshAndRetry(
            (token) => window.electron.library.getLikes(token, p, LIMIT)
        );
        if (!res || res.error) break;
        const rows = res.data || [];
        out.push(...rows);
        const total = res.pagination?.total || 0;
        if (rows.length < LIMIT || out.length >= total) break;
    }
    return out;
}

export const fetchPlaylistRecommendations = createAsyncThunk(
    'smartRecs/fetchPlaylistRecommendations',
    async ({ playlistId, variant }, { dispatch, getState }) => {
        try {
            const isLikesSource = playlistId === LIKES_SOURCE_ID;
            window.electron.analytics.track('smart_recs_search', {
                playlist_id: isLikesSource ? 'likes' : playlistId,
                source: isLikesSource ? 'likes' : 'playlist',
            });
            dispatch(setLoading(true));
            dispatch(setError(null));
            dispatch(setResults(null));

            // 1. Load source tracks. Two sources:
            //    - Liked Tracks: cloud likes (newest-first) → reversed to
            //      oldest-first so the recency window below is the array tail.
            //    - Playlist: signed in = cloud library, signed-out desktop =
            //      local SQLite. This must mirror how SmartRecsTab loaded the
            //      playlist list (also keyed on accessToken) so the selected id
            //      is fetched from the source it came from.
            const accessToken = getState().auth.accessToken;
            const canUseLocalDb = typeof window !== 'undefined' && window.electron && !!window.electron.db;
            let playlist;
            if (isLikesSource) {
                const RANDOM_POOL_EXTRA = 100;
                const likes = await loadRecentLikes(200 + RANDOM_POOL_EXTRA);
                if (!likes.length) {
                    dispatch(setError('You have no liked tracks yet. Like some tracks and try again.'));
                    dispatch(setLoading(false));
                    return;
                }
                playlist = {
                    name: 'Liked Tracks',
                    // Reverse newest-first → oldest-first; "most recent" is now
                    // the tail, matching the playlist add-order convention.
                    tracks: likes.slice().reverse(),
                };
            } else if (accessToken) {
                // refreshAndRetry: a stale token refreshes + retries instead of
                // reading as "Playlist is empty or not found".
                const result = await refreshAndRetry((token) => window.electron.library.getPlaylist(token, playlistId));
                playlist = result?.data || result;
                if (playlist?.error) playlist = null;
            } else if (canUseLocalDb) {
                playlist = await window.electron.db.playlists.get(playlistId, true);
            } else {
                dispatch(setError('Please sign in to use recommendations.'));
                dispatch(setLoading(false));
                return;
            }
            if (!playlist || !playlist.tracks || playlist.tracks.length === 0) {
                dispatch(setError(isLikesSource ? 'You have no liked tracks yet.' : 'Playlist is empty or not found.'));
                dispatch(setLoading(false));
                return;
            }

            // 2. Build source identifiers for each track. Prefer a music-info
            // public id (deterministic id resolution, no server-side name
            // matching or enrichment) — but ALSO send the track's name. The
            // server unions both lists and dedupes by internal id, so a valid
            // id makes the name a no-op — while a STALE id (the track was
            // merged/deleted by a music-DB dedupe pass after the playlist row
            // stored it) silently resolves to nothing, and without the name
            // fallback an all-ids playlist would yield sourceTracksFound=0 and
            // an instant, bogus "came up dry". Both lists are oldest-first
            // (playlist add order / reversed likes), so the tail is the MOST
            // RECENT. Dedupe each.
            const seenIds = new Set();
            const seenNames = new Set();
            const idEntries = [];   // music-info public ids
            const nameEntries = []; // "Artist - Title" (fallback + stale-id insurance)
            const pushName = (n) => {
                const key = n.toLowerCase();
                if (seenNames.has(key)) return;
                seenNames.add(key);
                nameEntries.push(n);
            };
            for (const track of playlist.tracks) {
                // MIX rows (playlists can hold mixes, identity = mix_public_id)
                // are not catalog tracks: their public id is a MIX id and their
                // "DJ - Mix title" name would miss (or worse, hit a same-named
                // track) in the recs engine's track matching. Never seed them.
                if (track.mix_public_id || track.mixPublicId) continue;
                const publicId = (track.track_public_id || track.publicId || track.trackPublicId || '').toString().trim();
                if (publicId && !seenIds.has(publicId)) {
                    seenIds.add(publicId);
                    idEntries.push(publicId);
                }
                const artist = (track.artist || track.albumArtist || '').trim();
                const title = (track.title || '').trim();
                let name = null;
                if (artist && title) name = `${artist} - ${title}`;
                else if (title) name = title;
                else if (artist) name = artist;
                else {
                    const fileName = (track.file_name || track.fileName || '').trim();
                    if (fileName) name = fileName.replace(/\.[^/.]+$/, '');
                }
                if (!name) continue;
                // Version-noise suffixes ("(Original Mix)" etc.) are handled
                // SERVER-side: getPlaylistRecommendationsService retries
                // unmatched seed names with the suffix stripped.
                pushName(name);
            }

            // Cap at the server's 200-item ceiling (Joi array.max — exceeding it
            // fails the whole call). Prefer id-backed tracks (guaranteed to
            // resolve), then fill any remaining budget with the most RECENT
            // names, shuffling a slightly larger recent window so repeat runs on
            // a big crate surface a bit of variety instead of the same set.
            const MAX_TOTAL = 200;
            const RANDOM_POOL_EXTRA = 100;
            const takeRecentWindow = (arr, budget) => {
                if (arr.length <= budget) return arr;
                const window = arr.slice(-Math.min(arr.length, budget + RANDOM_POOL_EXTRA));
                shuffleInPlace(window);
                return window.slice(0, budget);
            };

            let trackPublicIds = takeRecentWindow(idEntries, MAX_TOTAL);
            let trackNames = takeRecentWindow(nameEntries, Math.max(0, MAX_TOTAL - trackPublicIds.length));

            if (trackPublicIds.length === 0 && trackNames.length === 0) {
                dispatch(setError(
                    'No track names found in this playlist. Add tracks with an artist and title to use recommendations.'
                ));
                dispatch(setLoading(false));
                return;
            }

            // 3. Get DJ Pal token (auto-connect if needed; waits out an
            // in-flight background connection instead of failing fast)
            let token;
            try {
                token = await ensureToken(dispatch, getState);
            } catch {
                dispatch(setError('Could not connect to recommendations service. Please try again.'));
                dispatch(setLoading(false));
                return;
            }

            // 4. Call playlist recommendations
            let response = null;
            for (let attempt = 0; attempt <= 4; attempt++) {
                if (attempt) await new Promise((r) => setTimeout(r, 300 * attempt));
                response = await window.electron.djpal.playlistRecommendations(
                    token,
                    { trackNames, trackPublicIds },
                    'NODE2VEC',
                    variant || 'A'
                );
                // Cold start: main hasn't resolved the tier yet — wait it out.
                if (response?.error !== 'TIER_NOT_READY') break;
            }

            if (response.error) {
                // The server's error envelope for Joi failures looks like:
                //   { code: 'VALIDATION_ERROR', message: 'Validation error',
                //     details: [{ field, message, type }] }
                // Surfacing details[0].message turns the generic "Validation
                // error" toast into something actionable ("At least one track
                // name is required", "Maximum 200 track names allowed", etc).
                const err = response.error;
                let msg = 'Recommendations failed.';
                if (typeof err === 'string') {
                    msg = err;
                } else if (err) {
                    const detailMsg = Array.isArray(err.details) && err.details[0]?.message;
                    msg = detailMsg || err.message || msg;
                }
                dispatch(setError(msg));
                dispatch(setLoading(false));
                return;
            }

            const data = response.data || response;

            // Bundle playlist info with results so it persists in Redux
            data.sourcePlaylist = {
                name: playlist.name,
                tracks: playlist.tracks.map(t => ({
                    artist: t.artist || t.albumArtist || '',
                    title: t.title || '',
                    fileName: t.file_name || t.fileName || '',
                    filePath: t.file_path || t.filePath || '',
                    cover: t.cover || t.artwork || t.artworkUrl || t.image || '',
                })),
            };

            dispatch(setResults(data));
            dispatch(setLoading(false));
        } catch (error) {
            console.error('Smart Recs error:', error);
            dispatch(setError('Failed to fetch recommendations. Please try again.'));
            dispatch(setLoading(false));
        }
    }
);
